import { userAPI } from './api';
import { useAuthStore } from '../store/authStore';

export interface MessageAuthor {
  user_id: string;
  username?: string;
  display_name?: string | null;
  avatar_url?: string | null;
  is_system?: boolean;
  bot?: boolean;
}

type AuthorMessage = {
  user_id?: string;
  sender_id?: string;
  author?: MessageAuthor | null;
};

const profileCache = new Map<string, MessageAuthor>();
const inflightProfiles = new Map<string, Promise<MessageAuthor>>();

function currentUserAuthor(userId: string): MessageAuthor | null {
  const { user, profile } = useAuthStore.getState();
  if (!user || user.id !== userId) return null;

  return {
    user_id: user.id,
    username: profile?.username || user.username,
    display_name: profile?.display_name ?? user.display_name ?? null,
    avatar_url: profile?.avatar_url ?? user.avatar ?? null,
  };
}

function normalizeProfile(userId: string, profile: any): MessageAuthor {
  return {
    user_id: profile?.user_id || profile?.id || userId,
    username: profile?.username,
    display_name: profile?.display_name ?? null,
    avatar_url: profile?.avatar_url ?? profile?.avatar ?? null,
    is_system: profile?.is_system,
    bot: profile?.bot,
  };
}

async function loadAuthor(userId: string): Promise<MessageAuthor> {
  const current = currentUserAuthor(userId);
  if (current?.username) {
    profileCache.set(userId, current);
    return current;
  }

  const cached = profileCache.get(userId);
  if (cached) return cached;

  const existing = inflightProfiles.get(userId);
  if (existing) return existing;

  const request = userAPI
    .getUserProfile(userId)
    .then((profile) => {
      const author = normalizeProfile(userId, profile);
      profileCache.set(userId, author);
      return author;
    })
    .catch(() => ({ user_id: userId }))
    .finally(() => {
      inflightProfiles.delete(userId);
    });

  inflightProfiles.set(userId, request);
  return request;
}

export async function hydrateMessageAuthor<T extends AuthorMessage>(message: T): Promise<T> {
  const userId = message.author?.user_id || message.user_id || message.sender_id;
  if (!userId || message.author?.username || message.author?.display_name) {
    return message;
  }

  const author = await loadAuthor(userId);
  return { ...message, author };
}
